import React from 'react';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { NumericInput, ScanTextInput } from '../../../common/inputs';
import { UseCrossDockSuggestionResult } from './hooks/useCrossDockSuggestion';

interface CrossDockPanelProps {
  crossDock: UseCrossDockSuggestionResult;
  unit?: string;
}

/**
 * Crossdock-spørsmålet på den aktive ankomstlinjen (TW 36395306).
 * Pro-lager: Ja (crossdock-modus med lokasjon og antall) / Nei (vanlig ankomst).
 * Standard-lager: bare informasjon om restbehovet.
 * All tilstand ligger i useCrossDockSuggestion; komponenten rendres ikke uten forslag.
 */
export const CrossDockPanel: React.FC<CrossDockPanelProps> = ({ crossDock, unit }) => {
  const { t } = useTranslation();
  const { suggestion, decision, setDecision, crossDockBins, binCode, setBinCode, crossDockQty, setCrossDockQty, message } = crossDock;

  if (!crossDock.visible || !suggestion) {
    return message ? <p className="mt-2 text-sm text-amber-800">{message}</p> : null;
  }

  const unitText = unit || t('warehouse.general.unitDefault', {}, 'stk');
  const demandText = t('warehouse.modules.receipt.crossDock.demand',
    { '0': String(suggestion.demandQty), '1': unitText, '2': String(suggestion.numOfDays) },
    `Varen har restbehov på ${suggestion.demandQty} ${unitText} innen ${suggestion.numOfDays} dager.`);

  // Standard-lager: bare informasjon, ingen valg
  if (!suggestion.isAdvancedWarehouse) {
    return (
      <div className="mt-3 rounded-lg border border-blue-200 bg-blue-50 p-3">
        <p className="text-sm text-blue-800">{demandText}</p>
      </div>
    );
  }

  const handleBinScan = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const scanned = e.currentTarget.value.trim();
    const match = crossDockBins.find(b => b.toLowerCase() === scanned.toLowerCase());
    if (match) setBinCode(match);
    e.currentTarget.value = '';
  };

  return (
    <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
      <p className="text-sm font-medium text-amber-800">
        {demandText} {t('warehouse.modules.receipt.crossDock.question', {}, 'Legge på crossdock?')}
      </p>

      <div className="mt-2 flex gap-2">
        <button
          type="button"
          onClick={() => setDecision('yes')}
          className={`px-4 py-2 text-sm font-medium rounded-md border ${decision === 'yes' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300'}`}
        >
          {t('warehouse.modules.receipt.crossDock.yes', {}, 'Ja, crossdock')}
        </button>
        <button
          type="button"
          onClick={() => setDecision('no')}
          className={`px-4 py-2 text-sm font-medium rounded-md border ${decision === 'no' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300'}`}
        >
          {t('warehouse.modules.receipt.crossDock.no', {}, 'Nei, vanlig ankomst')}
        </button>
      </div>

      {decision === 'yes' && (
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm text-gray-700 mb-1">
              {t('warehouse.modules.receipt.crossDock.bin', {}, 'Crossdock-lokasjon')}
            </label>
            <select
              value={binCode}
              onChange={(e) => setBinCode(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white"
            >
              <option value="">{t('warehouse.modules.receipt.crossDock.chooseBinOption', {}, 'Velg lokasjon')}</option>
              {crossDockBins.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
            <ScanTextInput
              onKeyDown={handleBinScan}
              placeholder={t('warehouse.modules.receipt.crossDock.scanBin', {}, 'eller skann lokasjon')}
              className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">
              {t('warehouse.modules.receipt.crossDock.quantity', {}, 'Antall til crossdock')}
            </label>
            <NumericInput
              mode="integer"
              value={crossDockQty}
              onChange={(e) => setCrossDockQty(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
            <p className="mt-1 text-xs text-gray-500">
              {t('warehouse.modules.receipt.crossDock.restHint', {}, 'Resten blir stående på linjen for vanlig mottak.')}
            </p>
          </div>
        </div>
      )}

      {message && <p className="mt-2 text-sm text-amber-800">{message}</p>}
    </div>
  );
};
